// Heart Games Feed Item Component
// Displays Heart Games achievements, completions, and milestones

import React from "react";
import { motion } from "framer-motion";
import {
  FeedItemContent,
  ContentOverlay,
  UserInfo,
  UserAvatar,
  UserDetails,
  Username,
  Timestamp,
  ContentText,
  ContentTitle,
  ContentDescription,
  ActionButtons,
  ActionButton,
  ActionCount,
  SwipeModeToggle,
} from "../../../styles/SocialFeed/FeedContainer.styles";
import { HeartGamesItemProps, FeedInteraction } from "../../../types/socialFeed";

export const HeartGamesItem: React.FC<HeartGamesItemProps> = ({
  item,
  onInteraction,
  onUserClick,
  onSwipeModeTrigger,
  showSwipeOption = false,
  isFullScreen = false,
  aspectRatio,
}) => {
  const { content, engagement } = item;

  // Get achievement badge display
  const getAchievementDisplay = () => {
    if (content.achievement) {
      return {
        icon: getAchievementIcon(content.achievement.rarity),
        title: `${content.achievement.name}`,
        description: `${content.achievement.description}`,
        color: getAchievementColor(content.achievement.rarity),
      };
    }
    return null;
  };

  // Get game completion display
  const getGameCompletionDisplay = () => {
    if (content.gameResult) {
      return {
        icon: "🎮",
        title: `Completed ${content.gameType}`,
        description: `Score: ${content.score} | Level: ${content.level}`,
        color: "#667eea",
      };
    }
    return null;
  };

  // Get streak milestone display
  const getStreakDisplay = () => {
    if (content.streak) {
      return {
        icon: "🔥",
        title: `${content.streak} Day Streak!`,
        description: `Keep playing Heart Games to maintain your streak`,
        color: "#ff6b6b",
      };
    }
    return null;
  };

  // Get display data based on item type
  const getDisplayData = () => {
    switch (item.type) {
      case "heart-games-achievement":
        return getAchievementDisplay();
      case "heart-games-completion":
        return getGameCompletionDisplay();
      case "streak-milestone":
        return getStreakDisplay();
      default:
        return null;
    }
  };

  const displayData = getDisplayData();

  if (!displayData) {
    return null;
  }

  return (
    <FeedItemContent>
      {/* Background with gradient based on achievement type */}
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${displayData.color}20 0%, ${displayData.color}40 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Achievement Icon */}
        <motion.div
          style={{
            fontSize: "4rem",
            marginBottom: "1rem",
          }}
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            duration: 0.6, 
            delay: 0.2,
            type: "spring",
            stiffness: 200 
          }}
        >
          {displayData.icon}
        </motion.div>
      </motion.div>

      {/* Content Overlay */}
      <ContentOverlay>
        <UserInfo>
          <UserAvatar 
            src={item.avatar} 
            alt={item.username}
            onClick={() => onUserClick(item.userId)}
          />
          <UserDetails>
            <Username onClick={() => onUserClick(item.userId)}>
              {item.username}
            </Username>
            <Timestamp>
              {formatTimestamp(item.timestamp)}
            </Timestamp>
          </UserDetails>
        </UserInfo>

        <ContentText>
          <ContentTitle style={{ color: displayData.color }}>
            {displayData.title}
          </ContentTitle>
          <ContentDescription>
            {displayData.description}
          </ContentDescription>
          
          {/* Additional game-specific content */}
          {content.gameType && (
            <div style={{ 
              marginTop: "0.5rem",
              padding: "0.5rem",
              background: "rgba(255, 255, 255, 0.1)",
              borderRadius: "8px",
              fontSize: "0.8rem",
            }}>
              <strong>Game:</strong> {content.gameType}
              {content.level && (
                <span style={{ marginLeft: "1rem" }}>
                  <strong>Level:</strong> {content.level}
                </span>
              )}
            </div>
          )}

          {/* Achievement rarity badge */}
          {content.achievement && (
            <div style={{
              marginTop: "0.5rem",
              display: "inline-block",
              padding: "0.25rem 0.75rem",
              background: getAchievementColor(content.achievement.rarity),
              borderRadius: "12px",
              fontSize: "0.7rem",
              fontWeight: "600",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}>
              {content.achievement.rarity}
            </div>
          )}
        </ContentText>
      </ContentOverlay>

      {/* Action Buttons */}
      <ActionButtons>
        <ActionButton
          $variant="primary"
          $isActive={engagement.isLiked}
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "like",
            timestamp: new Date(),
          })}
        >
          {engagement.isLiked ? "❤️" : "🤍"}
        </ActionButton>
        <ActionCount>{engagement.likes}</ActionCount>

        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "comment",
            timestamp: new Date(),
          })}
        >
          💬
        </ActionButton>
        <ActionCount>{engagement.comments}</ActionCount>

        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "share",
            timestamp: new Date(),
          })}
        >
          📤
        </ActionButton>
        <ActionCount>{engagement.shares}</ActionCount>

        {/* Play Game Button */}
        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "view",
            timestamp: new Date(),
            metadata: { action: "play-game", gameType: content.gameType }
          })}
        >
          🎮
        </ActionButton>
      </ActionButtons>

      {/* Swipe Mode Toggle for high compatibility */}
      {showSwipeOption && onSwipeModeTrigger && (
        <SwipeModeToggle
          $show={true}
          onClick={() => onSwipeModeTrigger(item)}
        >
          💕 Interested?
        </SwipeModeToggle>
      )}
    </FeedItemContent>
  );
};

// Helper functions
const getAchievementIcon = (rarity: string): string => {
  switch (rarity) {
    case "common": return "🏆";
    case "rare": return "💎";
    case "epic": return "👑";
    case "legendary": return "⭐";
    default: return "🏆";
  }
};

const getAchievementColor = (rarity: string): string => {
  switch (rarity) {
    case "common": return "#95a5a6";
    case "rare": return "#3498db";
    case "epic": return "#9b59b6";
    case "legendary": return "#f39c12";
    default: return "#95a5a6";
  }
};

const formatTimestamp = (timestamp: Date): string => {
  const now = new Date();
  const diff = now.getTime() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  
  return new Date(timestamp).toLocaleDateString();
};
